import React, { useEffect, useRef, useState, useCallback } from 'react';
import { COLORS } from '../../utils/constants';
import { BarChart2, RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';

// Valid Binance USDT pairs that exist on TradingView
const VALID_BINANCE_SYMBOLS = new Set([
  'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'XRPUSDT', 'SOLUSDT', 'DOGEUSDT', 'ADAUSDT', 
  'AVAXUSDT', 'TRXUSDT', 'LINKUSDT', 'DOTUSDT', 'MATICUSDT', 'SHIBUSDT', 'LTCUSDT',
  'BCHUSDT', 'UNIUSDT', 'XLMUSDT', 'ATOMUSDT', 'XMRUSDT', 'ETCUSDT', 'NEARUSDT',
  'FILUSDT', 'APTUSDT', 'HBARUSDT', 'ARBUSDT', 'VETUSDT', 'MKRUSDT', 'GRTUSDT',
  'OPUSDT', 'AAVEUSDT', 'ALGOUSDT', 'FTMUSDT', 'SANDUSDT', 'MANAUSDT', 'AXSUSDT',
  'THETAUSDT', 'EOSUSDT', 'XTZUSDT', 'FLOWUSDT', 'SNXUSDT', 'CHZUSDT', 'CRVUSDT',
  'LDOUSDT', 'KAVAUSDT', 'ZECUSDT', 'NEOUSDT', 'DASHUSDT', 'BATUSDT', 'ENJUSDT',
  'COMPUSDT', 'ZILUSDT', 'ICXUSDT', 'QTUMUSDT', 'ONTUSDT', 'IOSTUSDT', 'HOTUSDT',
  'SCUSDT', 'DGBUSDT', 'RVNUSDT', 'ZENUSDT', 'ANKRUSDT', 'CELOUSDT', 'RSRUSDT',
  '1INCHUSDT', 'SUSHIUSDT', 'YFIUSDT', 'UMAUSDT', 'BNTUSDT', 'KNCUSDT', 'RENUSDT',
  'LRCUSDT', 'STORJUSDT', 'CVCUSDT', 'NMRUSDT', 'OGNUSDT', 'BANDUSDT', 'SKLUSDT',
  'CTSIUSDT', 'API3USDT', 'DODOUSDT', 'MASKUSDT', 'PERPUSDT', 'DYDXUSDT', 'IMXUSDT',
  'GALAUSDT', 'ENSUSDT', 'APEUSDT', 'GMTUSDT', 'MAGICUSDT', 'PEPEUSDT', 'FLOKIUSDT',
  'WLDUSDT', 'BLURUSDT', 'SUIUSDT', 'SEIUSDT', 'TIAUSDT', 'JUPUSDT', 'STRKUSDT',
  'PYTHUSDT', 'MANTAUSDT', 'PIXELUSDT', 'PORTALUSDT', 'AEVOUSDT', 'WUSDT', 'NOTUSDT',
  'IOUSDT', 'ZKUSDT', 'LISTAUSDT', 'ZROUSDT', 'RENDERUSDT', 'TAOUSDT', 'FETUSDT',
  'AGIXUSDT', 'OCEANUSDT', 'RNDRUSDT', 'ARKMUSDT', 'WOOUSDT', 'PENDLEUSDT', 'JTOUSDT',
  'BONKUSDT', 'WIFUSDT', 'ORDIUSDT', 'SATSUSDT', 'MEMEUSDT', 'RAYUSDT', 'INJUSDT',
  'RUNEUSDT', 'TONUSDT', 'STXUSDT', 'EGLDUSDT', 'ICPUSDT', 'KASUSDT', 'ROSEUSDT',
  'CFXUSDT', 'OSMOUSDT', 'MINAUSDT', 'KLAYUSDT', 'CAKEUSDT', 'WAVESUSDT',
]);

const TradingChart = ({ symbol }) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [symbolNotSupported, setSymbolNotSupported] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const initWidget = useCallback(() => {
    if (!containerRef.current || !isMounted) return;

    setIsLoading(true);
    setHasError(false);
    setSymbolNotSupported(false);

    // Check if symbol is supported on TradingView/Binance
    if (!VALID_BINANCE_SYMBOLS.has(symbol)) {
      setSymbolNotSupported(true);
      setIsLoading(false);
      return;
    }

    // Clean up existing widget
    if (widgetRef.current) {
      try {
        widgetRef.current.remove?.();
      } catch (e) {
        // Ignore cleanup errors
      }
      widgetRef.current = null;
    }

    // Clear the container
    const chartContainer = document.getElementById('tradingview_chart');
    if (chartContainer) {
      chartContainer.innerHTML = '';
    }

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    
    script.onload = () => {
      if (window.TradingView && containerRef.current && isMounted) {
        try {
          // Detect if mobile (window width <= 768)
          const isMobile = window.innerWidth <= 768;
          
          widgetRef.current = new window.TradingView.widget({
            autosize: true,
            symbol: `BINANCE:${symbol}`,
            interval: '15',
            timezone: 'Etc/UTC',
            theme: 'dark',
            style: '1',
            locale: 'ru',
            toolbar_bg: COLORS.card,
            enable_publishing: false,
            hide_side_toolbar: isMobile,  // Hide side toolbar on mobile for more space
            allow_symbol_change: false,
            container_id: 'tradingview_chart',
            backgroundColor: COLORS.background,
            gridColor: COLORS.border,
            withdateranges: true,
            hide_top_toolbar: false, // SHOW top toolbar for timeframes & indicators
            save_image: false,
            hide_legend: isMobile,  // Hide legend on mobile, show on desktop
            studies: [],  // Clean chart - no default indicators (user can add via toolbar)
          });
          
          // Hide loading after widget loads
          setTimeout(() => {
            if (isMounted) setIsLoading(false);
          }, 1500);
        } catch (e) {
          console.error('TradingView widget error:', e);
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    script.onerror = () => {
      setHasError(true);
      setIsLoading(false);
    };

    document.head.appendChild(script);
  }, [symbol, isMounted]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (isMounted) {
      initWidget();
    }

    return () => {
      try {
        if (widgetRef.current && widgetRef.current.remove) {
          widgetRef.current.remove();
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      widgetRef.current = null;
    };
  }, [symbol, isMounted, initWidget]);

  const handleRetry = () => {
    initWidget();
  };

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: COLORS.card,
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        position: 'relative',
        minHeight: '400px',
      }}
    >
      {/* Loading State */}
      {isLoading && !hasError && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          zIndex: 10,
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${COLORS.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <BarChart2 size={24} color={COLORS.primary} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Загрузка графика...
          </div>
          <div style={{
            width: '120px',
            height: '3px',
            backgroundColor: COLORS.border,
            borderRadius: '2px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: '40%',
              height: '100%',
              backgroundColor: COLORS.primary,
              borderRadius: '2px',
              animation: 'loadingBar 1.2s ease-in-out infinite',
            }} />
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          zIndex: 10,
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${COLORS.danger}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <AlertCircle size={24} color={COLORS.danger} />
          </div>
          <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
            Ошибка загрузки графика
          </div>
          <button
            onClick={handleRetry}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} />
            Повторить
          </button>
        </div>
      )}

      {/* Symbol Not Supported State */}
      {symbolNotSupported && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: COLORS.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 10,
          padding: '20px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${COLORS.warning || '#f0b90b'}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <AlertTriangle size={24} color={COLORS.warning || '#f0b90b'} />
          </div>
          <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
            График недоступен
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', maxWidth: '280px' }}>
            Пара {symbol} не поддерживается на TradingView. Торговля доступна, но без графика.
          </div>
        </div>
      )}

      <div id="tradingview_chart" style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: '100%', 
        width: '100%' 
      }} />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.95); }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(150%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
};

export default TradingChart;
